// Google Tag Manager. Browser-side only.
//
// Call sites push to `window.dataLayer` instead of touching it directly so a missing/blocked
// container (env var unset, script blocked) is always a silent no-op rather than a runtime
// error that could break the quiz flow — same convention as `trackMetaEvent()` in
// `lib/analytics/metaPixel.ts`.

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Build-time-inlined (NEXT_PUBLIC_*) — read once at module scope. Unset disables the
 * container entirely: see `components/analytics/GoogleTagManager.tsx` and
 * `components/analytics/GoogleTagManagerNoScript.tsx`. */
export const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;

export function pushToDataLayer(payload: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  try {
    // dataLayer is a queue by design — create it rather than no-op on a missing array, so an
    // event fired before the loader script has run (e.g. a very early route change) is
    // replayed once gtm.js processes the backlog, instead of being silently dropped.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  } catch {
    // Tracking must never break the app it's tracking.
  }
}

/** `window.gtag` is defined by the inline script in `GoogleTagManager.tsx` — it's a plain
 * `function gtag(){...}` declared inside a non-module <script> tag, so it lands on `window`
 * as a normal global. Calling through it (rather than pushing a raw array to `dataLayer`)
 * matches Google's own Consent Mode examples exactly and keeps the `arguments`-object shape
 * gtag.js's dataLayer processor expects. No-ops before the script has run or if it never
 * loaded (env var unset, blocked) — same never-throws convention as `pushToDataLayer`. */
export function callGtag(...args: unknown[]): void {
  if (typeof window === "undefined") return;
  try {
    window.gtag?.(...args);
  } catch {
    // Tracking must never break the app it's tracking.
  }
}

/** Google Consent Mode v2 signals (https://developers.google.com/tag-platform/security/guides/consent).
 * Kept as shared constants so the initial `consent default` block (set before `gtm.js` loads)
 * and the later `consent update` call can never drift apart — all four signals must move
 * together with the site's single cookie-consent choice (`lib/consent.ts`). */
export const GTM_CONSENT_DENIED = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
} as const;

export const GTM_CONSENT_GRANTED = {
  ad_storage: "granted",
  ad_user_data: "granted",
  ad_personalization: "granted",
  analytics_storage: "granted",
} as const;
